/**
 * Items and recipes (#180, D21): the shape a recipe book has.
 *
 * An **item** is the idea a guest orders — "Negroni", "Cheeseburger" — with
 * its story, style and tags. It has one or more **recipes**: the House one,
 * and any variants. A recipe is what is served: its lines are what is poured
 * or plated, glass, method, ice, garnish. Each recipe has its own version
 * history, price per venue and till item.
 *
 * Batches, syrups, juices and kitchen prep are not items. They are
 * house-made ingredients: an `Ingredient` with a `build` (recipes.ts).
 *
 * ## The spec view
 *
 * Everything that read the old one-record `Spec` — the menu, WhatTail, the
 * chat, the guest page, the till sync, every screen — keeps reading one,
 * assembled from an item and a recipe (`specView`). A recipe's id is the
 * spec id it replaced, so prices, sales, till mappings and wastage entries
 * keyed on it carry over untouched. Phase B moves the screens onto items
 * and recipes; Phase D removes the view.
 */
import { orgPrefix } from "./keys.js";
import { normaliseTags } from "./library.js";
export const ITEM_KINDS = ["drink", "food"];
export function isItemKind(value) {
    return value === "drink" || value === "food";
}
/** What an item's default recipe is called, where no better word exists. */
export const HOUSE_LABEL = "House";
/** The recipe label is capped: it is a word or two beside the item's name. */
export const MAX_RECIPE_LABEL = 60;
/**
 * The recipe's name as a person reads it: the item's for House, and the
 * item's with the label for a variant — "Negroni · Rum".
 */
export function recipeName(item, recipe) {
    return recipe.house || !recipe.label.trim() ? item.name : `${item.name} · ${recipe.label.trim()}`;
}
/**
 * The label a variant gets from a spec name that was its own: "Rum Negroni"
 * under Negroni is "Rum"; "Negroni Sbagliato" is "Sbagliato"; a name the
 * item's does not appear in ("Boulevardier") is kept whole.
 */
export function variantLabel(itemName, specName) {
    const item = itemName.trim();
    const name = specName.trim();
    if (!item || name.toLowerCase() === item.toLowerCase())
        return "";
    const at = name.toLowerCase().indexOf(item.toLowerCase());
    if (at < 0)
        return name;
    const rest = `${name.slice(0, at)} ${name.slice(at + item.length)}`.replace(/\s+/g, " ").replace(/^[\s·,-]+|[\s·,-]+$/g, "");
    return rest || name;
}
/**
 * The order an item's recipes are read in: the working recipe, then what is
 * live, then what is being tested, then what is retired.
 *
 * One rule, because two screens show the same family — the item's tabs and
 * the service deck's swipe — and a bartender who learns the order on one
 * should not have to learn it again on the other.
 */
export function recipeRank(recipe) {
    if (recipe.house)
        return 0;
    if (recipe.status === "available" || recipe.status === "not-active")
        return 1;
    return recipe.status === "needs-testing" ? 2 : 3;
}
/** The house recipe of an item, or any recipe of it when none is marked. */
export function houseRecipe(recipes, itemId) {
    const own = recipes.filter((r) => r.itemId === itemId);
    return own.find((r) => r.house) ?? own[0];
}
/**
 * One recipe as the old one-record spec, for every reader that still wants
 * one. `variantOf` is the item's House recipe for a variant, so the family
 * grouping the screens do keeps working; `batch` is the free text nobody
 * fills in any more.
 */
export function specView(item, recipe, houseId) {
    return {
        specId: recipe.recipeId,
        name: recipeName(item, recipe),
        status: recipe.status,
        style: item.style,
        // Both sets, as one list: the item's say what the drink is, the
        // recipe's say what this way of making it is (#188). Every reader of
        // `spec.tags` — the library's filter, menu engineering, the chat —
        // then finds a variant by its own label without knowing there are two
        // places to look. `normaliseTags` dedupes, so a tag on both is one.
        //
        // Both are read defensively because every row written before #188 has
        // no `tags` on its recipe, and the view is assembled straight off the
        // stored row.
        tags: normaliseTags([...(item.tags ?? []), ...(recipe.tags ?? [])]),
        method: recipe.method,
        glass: recipe.glass,
        ice: recipe.ice,
        garnish: recipe.garnish,
        batch: "",
        creator: item.creator,
        added: item.added,
        history: item.history,
        notes: recipe.notes,
        sundries: recipe.sundries,
        sheetGp: recipe.sheetGp,
        talk: recipe.talk ?? null,
        lines: recipe.lines,
        variantOf: recipe.house || !houseId || houseId === recipe.recipeId ? null : houseId,
        version: recipe.version,
        updatedAt: recipe.updatedAt,
        updatedBy: recipe.updatedBy,
        source: recipe.source,
        sourceText: recipe.sourceText,
        reviewed: recipe.reviewed,
    };
}
/** Every recipe as a spec view, in recipe order. A recipe whose item is missing is left out. */
export function specViews(items, recipes) {
    const byId = new Map(items.map((i) => [i.itemId, i]));
    return recipes.flatMap((recipe) => {
        const item = byId.get(recipe.itemId);
        if (!item)
            return [];
        return [specView(item, recipe, houseRecipe(recipes, recipe.itemId)?.recipeId)];
    });
}
/** The items and recipes among an org partition's rows. */
export function itemsAndRecipesOf(rows) {
    const of = (prefix) => rows.filter((row) => String(row["SK"] ?? "").startsWith(prefix));
    return { items: of(orgPrefix.items), recipes: of(orgPrefix.recipes) };
}
/** The spec views an org partition's rows make. What every "read the specs" became. */
export function specViewsOf(rows) {
    const { items, recipes } = itemsAndRecipesOf(rows);
    return specViews(items, recipes);
}
/** The fields of a spec body that belong to the item, and those that belong to the recipe. */
export const ITEM_FIELDS = ["name", "style", "tags", "creator", "added", "history"];
export const RECIPE_FIELDS = ["status", "method", "glass", "ice", "garnish", "notes", "sundries", "sheetGp", "talk", "lines"];
/**
 * An old-shaped spec body split into what goes on the item and what goes on
 * the recipe, for the `/v1/specs` routes kept through the transition.
 */
export function splitSpecBody(body) {
    return {
        item: { name: body.name, style: body.style, tags: body.tags, creator: body.creator, added: body.added, history: body.history },
        recipe: {
            status: body.status,
            method: body.method,
            glass: body.glass,
            ice: body.ice,
            garnish: body.garnish,
            notes: body.notes,
            sundries: body.sundries,
            sheetGp: body.sheetGp,
            talk: body.talk ?? null,
            lines: body.lines,
        },
    };
}
/**
 * What changed between two builds, in words a bartender reads: "+ 15 mL
 * Campari", "− Sweet vermouth", "Gin 30 → 45 mL". A variant is shown as
 * what differs from House, and a version as what differs from the one
 * before, because that is how a change is learnt.
 */
export function recipeDiff(before, after, ingredientsById) {
    const name = (id) => ingredientsById.get(id)?.name ?? id;
    const amount = (l) => l.unit === "dash" || l.unit === "drop" ? `${l.qty ?? 1} ${l.unit}` : l.unit === "top" ? "top" : `${+l.ml.toFixed(1)} mL`;
    const sum = (lines) => {
        const m = new Map();
        for (const l of lines) {
            const held = m.get(l.ingredientId);
            m.set(l.ingredientId, held ? { ...held, ml: held.ml + l.ml, qty: (held.qty ?? 0) + (l.qty ?? 0) || undefined } : l);
        }
        return m;
    };
    const a = sum(before);
    const b = sum(after);
    const out = [];
    for (const [id, line] of b) {
        const was = a.get(id);
        if (!was)
            out.push(`+ ${amount(line)} ${name(id)}`);
        else if (Math.abs(was.ml - line.ml) > 0.05 || was.unit !== line.unit)
            out.push(`${name(id)} ${amount(was)} → ${amount(line)}`);
    }
    for (const id of a.keys())
        if (!b.has(id))
            out.push(`− ${name(id)}`);
    return out;
}
/** Which recipe statuses an item counts as live under. */
export function isLiveStatus(status) {
    return status === "available" || status === "needs-testing";
}
//# sourceMappingURL=items.js.map